import { EyeIcon, FileIcon, PencilIcon, Trash2Icon, XIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@krea/shared/design-system/Button";
import { FileDropArea } from "@krea/shared/design-system/file-drop-area";
import {
  type FileUploadActions,
  type FileWithPreview,
  isImage,
} from "@krea/shared/hooks/useFileUpload";
import { cn } from "@krea/shared/utils";

import { CropModal } from "./CropModal";

export type PreviewStyle = "grid" | "list";

interface IFilePreviewProps {
  actions: FileUploadActions;
  files: FileWithPreview[];
  accept: string;
  className?: string;
  multiple: boolean;
  buttonLabel?: string;
}

// Hardcoded English like the rest of FileInput: its consumers are admin and
// lender, and lender loads no `common` namespace for FileDropArea's defaults.
const dropHint = (multiple: boolean) =>
  multiple
    ? "Drag files here or click to browse"
    : "Drag a file here or click to browse";

// The picker honours `multiple`, but a drop does not — in single-file mode
// only the first dropped file counts, as useFileUpload's own drop handler did.
const addPicked = (
  actions: FileUploadActions,
  picked: File[],
  multiple: boolean,
) => actions.addFiles(multiple ? picked : picked.slice(0, 1));

export const ListFilePreview = ({
  actions,
  files,
  accept,
  className,
  multiple,
  buttonLabel = "Select image",
}: IFilePreviewProps) => {
  const { removeFile } = actions;
  const hasFiles = files.length > 0;

  return (
    <div className="relative">
      <FileDropArea
        label={buttonLabel}
        hint={dropHint(multiple)}
        acceptedFileTypesMessage={null}
        accept={accept}
        multiple={multiple}
        // A single-file input with its file chosen stays visible but inert —
        // the file has to be removed before another can be picked.
        disabled={hasFiles && !multiple}
        compact={hasFiles && multiple}
        onFiles={(picked) => addPicked(actions, picked, multiple)}
        className={className}
      />
      {hasFiles && (
        <div className="mt-2 flex flex-col gap-2">
          {files.map((file) => (
            <div
              key={file.id}
              className="flex items-center justify-between gap-2 rounded-md bg-primary-150 p-2"
            >
              <div className="flex items-center text-sm font-medium">
                <FileIcon className="mr-1 size-4" />
                <span>{file.file.name}</span>
              </div>
              <div className="flex items-center gap-3">
                <EyeIcon
                  className="mr-1 size-4 cursor-pointer text-primary"
                  onClick={() => window.open(file.preview, "_blank")}
                />

                <Trash2Icon
                  className="size-4 cursor-pointer text-red"
                  onClick={() => removeFile(file.id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ImageFilePreview = ({
  fileWithImage,
  addFiles,
  removeFile,
}: {
  fileWithImage: FileWithPreview;
  addFiles: (files: File[], isEditing?: boolean) => void;
  removeFile: (id: string) => void;
}) => {
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);

  return (
    <div className="relative aspect-square rounded-md">
      {isCropModalOpen && (
        <CropModal
          file={fileWithImage}
          isOpen={isCropModalOpen}
          addFile={(file) => {
            addFiles([file], true);
          }}
          removeFile={(id) => removeFile(id)}
          setIsOpen={setIsCropModalOpen}
        />
      )}

      <img
        src={fileWithImage.preview}
        alt={fileWithImage.file.name}
        className="size-full rounded-[inherit] object-cover"
      />
      <div className="absolute right-2 top-2 flex gap-1">
        <Button
          size="icon"
          className="size-6 rounded-full shadow-none"
          onClick={(e) => {
            e.stopPropagation();
            setIsCropModalOpen(true);
          }}
        >
          <PencilIcon className="size-3.5" strokeWidth={2.5} />
        </Button>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            removeFile(fileWithImage.id);
          }}
          size="icon"
          className="size-6 rounded-full shadow-none"
          aria-label="Remove image"
        >
          <XIcon className="size-3.5" strokeWidth={2.5} />
        </Button>
      </div>
    </div>
  );
};

const OtherFilePreview = ({
  file,

  removeFile,
}: {
  file: FileWithPreview;
  addFiles: (files: File[]) => void;
  removeFile: (id: string) => void;
}) => {
  return (
    <div className="relative flex flex-col items-center justify-center rounded-md bg-primary-150 p-2">
      <FileIcon className="size-full rounded-[inherit] object-cover text-primary" />
      <span className="text-xs font-medium">{file.file.name}</span>
      <Button
        size="icon"
        className="absolute right-2 top-2 size-6 rounded-full shadow-none"
        onClick={(e) => {
          e.stopPropagation();
          removeFile(file.id);
        }}
      >
        <XIcon className="size-3.5" strokeWidth={2.5} />
      </Button>
    </div>
  );
};

export const GridFilePreview = ({
  actions,
  files,
  accept,
  className,
  multiple,
  buttonLabel = "Select image",
}: IFilePreviewProps) => {
  const { removeFile, addFiles } = actions;

  if (files.length === 0) {
    return (
      <FileDropArea
        label={buttonLabel}
        hint={dropHint(multiple)}
        acceptedFileTypesMessage={null}
        accept={accept}
        multiple={multiple}
        onFiles={(picked) => addPicked(actions, picked, multiple)}
        className={className}
      />
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {multiple && (
        <div className="flex items-center justify-between gap-2">
          <span>Uploading {files.length} files</span>
          <FileDropArea
            asButton
            label={buttonLabel}
            accept={accept}
            multiple
            onFiles={(picked) => addPicked(actions, picked, multiple)}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {files.map((file) => (
          <div
            key={file.id}
            className={cn("relative aspect-square rounded-md", {
              "col-span-3": !multiple,
            })}
          >
            {isImage(file) ? (
              <ImageFilePreview
                fileWithImage={file}
                addFiles={addFiles}
                removeFile={removeFile}
              />
            ) : (
              <OtherFilePreview
                file={file}
                addFiles={addFiles}
                removeFile={removeFile}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
